<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1250"
  >
    <ChangeTargetsDialog
      :dialog="showChangeTargetDialog"
      :aligner="aligner"
      @close="closeChangeTargetDialog"
    >
    </ChangeTargetsDialog>

    <SupportTicketDialog
      :dialog="showSupportTicketDialog"
      :aligner="aligner"
      @close="closeSupportTicketDialog"
    >
    </SupportTicketDialog>

    <ResetSerialNumberDialog
      :dialog="showResetSerialNumberDialog"
      :currentSerialNumber="aligner.serialNumber"
      @close="closeResetSerialNumberDialog"
    >
    </ResetSerialNumberDialog>

    <v-card>
      <v-toolbar
        dark
      >
        <v-toolbar-title>
          <div>
            <div>{{aligner.serialNumber}}</div>
            <div style="font-size: 12px; font-weight: normal"><i>{{decodedSerialNumber}}</i></div>
          </div>
        </v-toolbar-title>
        <div class="flex-grow-1"></div>
        <v-btn icon>
          <v-badge
            v-model="showBadge"
            overlap
            color="amber"
          >
            <template
              v-slot:badge
            >
              <span>{{aligner.openSupportTickets}}</span>
            </template>
            <v-icon>bug_report</v-icon>
          </v-badge>
        </v-btn>
        <v-btn icon @click="toggleFavorite()">
          <v-icon>{{aligner.favorite ? 'favorite' : 'favorite_border'}}</v-icon>
        </v-btn>
        <v-btn icon @click="onRefreshClick()">
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn icon @click="onCloseClick()">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-row>
          <v-col>
            <div>Software Version:</div>
            <div>Software Ready to Download:</div>
            <div v-if="canShowSpecsVersion">
              <div>Specs Version:</div>
              <div>Specs Expiration:</div>
            </div>
            <div v-if="aligner.systemClass">System Class:</div>
            <div v-if="aligner.boardClass">Board Class:</div>
            <div v-if="canShowLoggingLevel">Logging Level:</div>
            <div v-if="canShowBoardFeatures">
              <div>Camera Count:</div>
              <div>Accelerometer Count:</div>
              <div>DD Enabled:</div>
            </div>
            <div>Connection Count:</div>
            <div>Connected:</div>
            <div>Remote Port:</div>
            <div>Address:</div>
            <div>Current Procedure:</div>
          </v-col>
          <v-col>
            <div class="data">
              <div v-if="aligner.version" :style="versionStyle">{{aligner.version.semantic}} {{aligner.version.meta}}</div>
              <div>{{aligner.pendingSoftwareDownload || 'NONE'}}</div>
              <div v-if="canShowSpecsVersion">
                <div v-if="aligner.specs">{{`${aligner.specs.name} ${aligner.specs.month}/${aligner.specs.year}`}}</div>
                <div v-else>unknown</div>
                <div v-if="aligner.specsExpiration">{{`${aligner.specsExpiration.month}/${aligner.specsExpiration.year}`}}</div>
                <div v-else>unknown</div>
              </div>
              <div v-if="aligner.systemClass">{{aligner.systemClass}}</div>
              <div v-if="aligner.boardClass">{{aligner.boardClass}}</div>
              <div v-if="canShowLoggingLevel">{{aligner.logLevel || '...'}}</div>
              <div v-if="canShowBoardFeatures">
                <div>{{aligner.boardFeatures.numCameras}}</div>
                <div>{{aligner.boardFeatures.numAccel}}</div>
                <div>{{aligner.boardFeatures.isDDAligner}}</div>
              </div>
              <div>{{aligner.connectCount || 0}}</div>
              <div>{{aligner.connected ? 'YES' : 'NO'}}</div>
              <div v-if="aligner.connected">
                <div>{{aligner.port || ' '}}</div>
                <div>{{aligner.address ? aligner.address.split(',')[0].slice(7) : ' '}}</div>
                <div>{{aligner.currentProcedure || ' '}}</div>
              </div>
              <div v-else>
                <div><i>disconnected</i></div>
                <div><i>disconnected</i></div>
                <div><i>disconnected</i></div>
              </div>
            </div>
          </v-col>
          <v-col>
            <div>Continent:</div>
            <div>Country:</div>
            <div>Region:</div>
            <div>City:</div>
            <div>Time Zone Name:</div>
            <div>Time Zone Offset:</div>
            <div>Local Time:</div>
            <br/>
            <div v-if="canShowUpdateStatus">Update State:</div>
          </v-col>
          <v-col class="data">
            <div>{{aligner.geolocation ? aligner.geolocation.continent_name : '-'}}</div>
            <div>{{aligner.geolocation ? aligner.geolocation.country_name : '-'}}</div>
            <div>{{aligner.geolocation ? aligner.geolocation.region : '-'}}</div>
            <div>{{aligner.geolocation ? aligner.geolocation.city : '-'}}</div>
            <div>{{aligner.geolocation ? aligner.geolocation.time_zone.name : '-'}}</div>
            <div>{{aligner.geolocation ? aligner.geolocation.time_zone.offset : '-'}}</div>
            <div>{{aligner.geolocation ? getLocalTime(aligner.geolocation.time_zone.offset) : '0000'}}</div>
            <br/>
            <div v-if="canShowUpdateStatus">{{updateState}}</div>
          </v-col>
          <v-col>
            <v-card v-if="aligner.connected && canShowUpdateStatus" height="300" flat>
              <div style="position: absolute; top: 0; width: 100%; text-align: right;">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn color="black" text icon x-small @click="onCheckNow()" v-on="on" ><v-icon x-small>cloud</v-icon></v-btn>
                  </template>
                  <span>Check Cloud</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn color="black" text icon x-small @click="onDownload()" v-on="on"><v-icon x-small>arrow_downward</v-btn>
                  </template>
                  <span>Download Updates</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn color="black" text icon x-small @click="onInstall()" v-on="on"><v-icon x-small>system_update_alt</v-icon></v-btn>
                  </template>
                  <span>Install Updates</span>
                </v-tooltip>
              </div>
            </v-card
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn
          color="green"
          class
          v-if="aligner.connected && canOpenPort"
          @click="onOpenPort()"
        >
          Open Port
        </v-btn>

        <!--
        <v-btn
          color="green"
          v-if="aligner.connected && canGetCalibrationLogs"
          @click="onGetCalibrationLogs()"
          :loading="gettingCalibrationLogs"
        >
          Cal Logs
        </v-btn>
        -->

        <v-btn
          color="green"
          v-if="aligner.connected && canOpenPort && canResetSerialNumber"
          @click.stop="showResetSerialNumberDialog = true"
          :loading="resettingSerialNumber"
        >
          Reset Serial
        </v-btn>
        <v-btn
          color="green"
          v-if="aligner.connected && canActivate"
          @click="onActivate"
          :loading="activating"
        >
          Activate
        </v-btn>
        <v-btn
          color="green"
          v-if="aligner.connected && canGetUsbStatus"
          @click="onGetUsbStatus"
          :loading="gettingUsbStatus"
        >
          USB
        </v-btn>
        <v-btn
          color="green"
          v-if="aligner.connected && canGetPrinterStatus"
          @click="onGetPrinterStatus"
          :loading="gettingPrinterStatus"
        >
          Printer
        </v-btn>
        <v-btn
          color="green"
          v-if="aligner.connected && canGetVoltages"
          @click="onGetVoltages"
          :loading="gettingVoltages"
        >
          Voltages
        </v-btn>
        <v-btn
          color="green"
          v-if="aligner.connected && canShowLoggingLevel"
          @click="onSetLogLevel"
          :loading="settingLogLevel"
        >
          {{aligner.logLevel === 'WARN' ? 'RAISE LOG LEVEL' : 'LOWER LOG LEVEL'}}
        </v-btn>
        <v-btn
          color="green"
          v-if="aligner.connected && canShowRcp && canOpenPort"
          @click="onRcp"
          :loading="settingRcp"
        >
          RCP
        </v-btn>

        <v-btn
          color="green"
          v-if="aligner.connected && canShowChangeTargetButton"
          @click.stop="showChangeTargetDialog = true"
          :loading="changingTargets"
        >
          Change Targets
        </v-btn>

        <v-btn
          color="green"
          v-if="aligner.connected && canShowClearPendingUpdatesButton"
          @click="onClearPendingUpdates()"
          :loading="clearingPendingUpdates"
        >
          Clear Updates
        </v-btn>

        <div class="flex-grow-1"></div>

        <v-btn
          color="indigo"
          v-if="canStage"
          class="white--text"
          @click="onStageUpdate()"
        >
          Stage {{latestSoftwareVersion}}
        </v-btn>

        <v-btn
          color="amber"
          v-if="canUnstage"
          class="grey lighten-1"
          @click="onUnstageUpdate()"
        >
          Unstage {{aligner.pendingSoftwareDownload}}
        </v-btn>

        <v-btn
          color="green"
          v-if="aligner.connected && canTakeScreenShot"
          @click="onGetScreenShot()"
          :loading="gettingScreenShot"
        >
          Screen Shot
        </v-btn>
        <v-btn
          color="amber"
          @click.stop="showSupportTicketDialog = true"
        >
          Open Ticket
        </v-btn>
        <v-btn
          color="grey lighten-1"
          @click="onCloseClick()"
        >
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar
      v-model="snackbar"
      timeout="3000"
    >
      {{ snackbarText }}
      <v-btn
        color="pink"
        text
        @click="snackbar = false"
      >
        CLOSE
      </v-btn>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import semver from 'semver';
import moment from 'moment';
import decodeSerialNumber from '../../decodeSerialNumber';
import SupportTicketDialog from '../support_tickets/SupportTicketDialog.vue';
import ResetSerialNumberDialog from './ResetSerialNumberDialog.vue';
import ChangeTargetsDialog from './ChangeTargetsDialog.vue';

const FAMILY_CONFIG = Object.freeze({
  gtps:            { storeKey: 'latestGtpsSoftwareVersion',  minVersion: null },
  aligner_oasis:   { storeKey: 'latestOasisSoftwareVersion', minVersion: null },
  'aligner-oasis': { storeKey: 'latestOasisSoftwareVersion', minVersion: null }, // tolerate both until confirmed
  aligner:         { storeKey: 'latestSoftwareVersion',      minVersion: '4.0.0' },
});

// pull a comparable version out of anything: string, {semantic}, or junk
function toVersion(input) {
  if (!input) return null;
  const raw = typeof input === 'string'
    ? input
    : (input.semantic || (input.version && input.version.semantic));
  return raw ? semver.coerce(raw) : null;   // coerce strips _, space, -rc.N, build meta
}

export default {
  components: {
    ChangeTargetsDialog,
    SupportTicketDialog,
    ResetSerialNumberDialog,
  },

  props: {
    dialog: Boolean,
    aligner: Object,
  },

  data() {
    return {
      showChangeTargetDialog: false,
      showSupportTicketDialog: false,
      showResetSerialNumberDialog: false,
      showBadge: false,
      snackbarText: '',
      snackbar: false,
      resettingSerialNumber: false,
      gettingCalibrationLogs: false,
      gettingScreenShot: false,
      activating: false,
      gettingUsbStatus: false,
      gettingPrinterStatus: false,
      gettingVoltages: false,
      settingLogLevel: false,
      settingRcp: false,
      changingTargets: false,
      clearingPendingUpdates: false,
    };
  },

  computed: {
    canOpenPort() {
      return (
        this.$store.state.auth.token
        && (
          this.$store.state.auth.token.user.role === 'admin'
          || this.$store.state.auth.token.user.role === 'engineer'
        )
      );
    },

    canShowChangeTargetButton() {
      return (this.$store.state.auth.token && (this.$store.state.auth.token.user.role === 'admin' || this.$store.state.auth.token.user.role === 'engineer' || this.$store.state.auth.token.user.role === 'support_lvl2') && (this.aligner && this.aligner.boardFeatures && this.aligner.boardFeatures.numCameras === 2 && (this.aligner.systemClass === 'DRAGON' || this.aligner.systemClass === 'DRAGON_HEADLESS')));
    },

    canShowClearPendingUpdatesButton() {
      return (this.$store.state.auth.token && (this.$store.state.auth.token.user.role === 'admin' || this.$store.state.auth.token.user.role === 'engineer' || this.$store.state.auth.token.user.role === 'support_lvl2'));
    },

    canShowUpdateStatus() {
      const a = this.aligner;
      if (!a) { console.log('[updateStatus] aligner is', a); return false; }

      const raw = a.version && a.version.semantic;
      const family = typeof a.family === 'string' ? a.family.trim().toLowerCase() : a.family;
      const coerced = raw ? semver.coerce(raw) : null;

      console.log('[updateStatus]', {
        rawSemantic: raw,
        coerced: coerced && coerced.version,
        family: JSON.stringify(a.family),
        keys: Object.keys(a),
        reactive: Object.getOwnPropertyDescriptor(a, 'version'),
      });

      if (!coerced) return false;

      const minimums = { aligner: '4.3.0', gtps: '1.0.0', 'aligner-oasis': '1.0.0' };
      const min = minimums[family];
      return Boolean(min) && semver.gte(coerced, min);
    },

    canGetUsbStatus() {
      let showIt = false;

      /*
      if (this.aligner && this.aligner.version && this.aligner.family) {
        if (
          (this.aligner.family === 'aligner' && semver.gte(this.aligner.version.semantic, '5.0.0'))
        ) {
          showIt = true;
        }
      }
      */

      return showIt;
    },

    canGetPrinterStatus() {
      let showIt = false;

      /*
      if (this.aligner && this.aligner.version && this.aligner.family) {
        if (
          (this.aligner.family === 'aligner' && semver.gte(this.aligner.version.semantic, '5.0.0'))
        ) {
          showIt = true;
        }
      }
      */

      return showIt;
    },

    canGetVoltages() {
      let showIt = false;

      /*
      if (this.aligner && this.aligner.version && this.aligner.family) {
        if (
          (this.aligner.family === 'aligner' && semver.gte(this.aligner.version.semantic, '5.0.0'))
        ) {
          showIt = true;
        }
      }
      */

      return showIt;
    },

    canGetCalibrationLogs() {
      return (this.aligner.family === 'gtps');
    },

    canResetSerialNumber() {
      return (this.aligner.version && semver.gte(this.aligner.version.semantic, '4.3.0')) || (this.aligner.family === 'gtps') || (this.aligner.family === 'aligner-oasis');
    },

    canTakeScreenShot() {
      return (this.aligner.version && semver.gte(this.aligner.version.semantic, '4.3.0') && this.aligner.systemClass !== 'DRAGON_HEADLESS') || (this.aligner.family === 'gtps') || (this.aligner.family === 'aligner-oasis');
    },

    canActivate() {
      return (this.aligner.version && semver.gte(this.aligner.version.semantic, '4.4.0')) || (this.aligner.family === 'gtps') || (this.aligner.family === 'aligner-oasis');
    },

    decodedSerialNumber() {
      if (this.aligner.serialNumber) {
        const decodedSerialNumber = decodeSerialNumber(this.aligner.serialNumber.toUpperCase());
        if (decodedSerialNumber) {
          return `${decodedSerialNumber.marketingName} ${decodedSerialNumber.buildMonth}/${decodedSerialNumber.buildYear}`;
        }
      }

      return '...';
    },

    updateState() {
      let result = '';

      if (this.aligner.updateState) {
        switch (this.aligner.updateState.state) {
          case 'BUSY':
            result = 'Busy';
            break;
          case 'TRY_INSERT_DIFFERENT_UPDATE':
          case 'TRY_INSERT_UPDATE':
            result = 'Nothing Pending';
            break;
          case 'READY_TO_DOWNLOAD':
            result = 'Pending Download';
            break;
          case 'DOWNLOADING':
            result = 'Downloading';
            break;
          case 'READY_TO_INSTALL':
            result = `Install Pending [${this.aligner.updateState.softwareUpdateDownload ? this.aligner.updateState.softwareUpdateDownload.version.semantic : '???'}]`;
            break;
          case 'VALIDATING_UPDATE':
            result = 'Validating Update Contents';
            break;
          case 'INSTALLING_UPDATE':
            result = 'Installing';
            break;
          default:
            result = this.aligner.updateState.state;
            break;
        }
      }

      return result;
    },

    versionStyle() {
      let style = 'color:green;';

      if (this.aligner.family === 'gtps') {
        if (semver.gt(this.$store.state.latestGtpsSoftwareVersion.semantic, this.aligner.version.semantic.split(/_| /)[0])) {
          style = 'color:red;';
        }
      } else if (this.aligner.family === 'aligner-oasis') {
        if (semver.gt(this.$store.state.latestOasisSoftwareVersion.semantic, this.aligner.version.semantic.split(/_| /)[0])) {
          style = 'color:red;';
        }
      } else if (semver.gt(this.$store.state.latestSoftwareVersion.semantic, this.aligner.version.semantic.split(/_| /)[0])) {
        style = 'color:red;';
      }

      return style;
    },

    latestSoftwareVersion() {
      if (this.aligner.family === 'gtps') {
        return this.$store.state.latestGtpsSoftwareVersion.semantic;
      }

      if (this.aligner.family === 'aligner-oasis') {
        return this.$store.state.latestOasisSoftwareVersion.semantic;
      }

      return this.$store.state.latestSoftwareVersion.semantic;
    },

    canShowRcp() {
      let result = false;

      if (this.aligner.family) {
        const v = this.aligner.version.semantic.split(/_| /)[0];
        if (semver.gte(v, '5.0.0') && this.aligner.boardFeatures && this.aligner.boardFeatures.isDDAligner) {
          result = true;
        }
      }

      return result;
    },

    canShowLoggingLevel() {
      let result = false;

      if (this.aligner.family) {
        const v = this.aligner.version.semantic.split(/_| /)[0];
        if (this.aligner.family === 'aligner-oasis') {
          if (semver.gte(v, '1.0.0')) {
            result = true;
          }
        } else if (this.aligner.family === 'gtps') {
          if (semver.gte(v, '1.2.0')) {
            result = true;
          }
        } else if (semver.gte(v, '5.0.0')) {
          result = true;
        }
      }

      return result;
    },

    canShowBoardFeatures() {
      let result = false;

      if (this.aligner.family) {
        const v = this.aligner.version.semantic.split(/_| /)[0];
        if (semver.gte(v  , '5.0.0') && this.aligner.boardFeatures) {
          result = true;
        }
      }

      return result;
    },

    canShowSpecsVersion() {
      let result = false;

      if (this.aligner.family) {
        const v = this.aligner.version.semantic.split(/_| /)[0];
        if (this.aligner.family === 'gtps') {
          if (semver.gte(v, '1.3.0')) {
            result = true;
          }
        } else if (semver.gte(v, '5.0.0')) {
          result = true;
        }
      }

      return result;
    },

    canStage() {
      const aligner = this.aligner;
      if (!aligner) return false;

      const family = String(aligner.family || '').trim().toLowerCase();
      const config = FAMILY_CONFIG[family] || FAMILY_CONFIG.aligner;

      const latest = toVersion(this.$store.state[config.storeKey]);
      if (!latest) return false;              // store not loaded — no throw

      const current = toVersion(aligner.version);
      if (!current) return false;

      if (config.minVersion && semver.lt(current, config.minVersion)) return false;
      if (semver.gte(current, latest)) return false;

      // != null is deliberate: catches null AND undefined
      const pending = toVersion(aligner.pendingSoftwareDownload);
      if (pending && semver.gte(pending, latest)) return false;

      const sud = aligner.updateState != null ? aligner.updateState.softwareUpdateDownload : null;
      const downloaded = sud != null ? toVersion(sud.version) : null;

      console.log('downloaded', downloaded);
      console.log('latest', latest);

      if (downloaded && semver.gte(downloaded, latest)) {
        console.log('returning false');
        return false;
      }

      console.log('returning true');

      return true;
    },

    canUnstage() {
      return this.aligner.pendingSoftwareDownload || false;
    },
  },

  beforeUpdate() {
    this.showBadge = this.aligner.openSupportTickets > 0;
  },

  methods: {
    getLocalTime(offset) {
      let l = '-';

      if (offset) {
        console.log(`local time offset: ${offset}`);
        l = moment().utcOffset(offset).format('ddd MMMM Do, YYYY HH:mm:ss');
      }

      return l;
    },

    onRefreshClick() {
      this.$emit('refresh');
    },

    onCloseClick() {
      this.$emit('close');
    },

    onGetUsbStatus() {
      this.gettingUsbStatus = true;

      const options = {
        method: 'post',
        url: '/cmd/aligner_get_usb_status/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options)
        .then((res) => {
          this.gettingUsbStatus = false;
        })
        .catch(() => {
          console.error('get_usb_status response failed');
        });
    },

    onGetPrinterStatus() {
      this.gettingPrinterStatus = true;

      const options = {
        method: 'post',
        url: '/cmd/aligner_get_printer_status/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options)
        .then((res) => {
          this.gettingPrinterStatus = false;
        })
        .catch(() => {
          console.error('get_printer_status response failed');
        });
    },

    onGetVoltages() {
      this.gettingVoltages = true;

      const options = {
        method: 'post',
        url: '/cmd/aligner_get_voltages/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options)
        .then((res) => {
          this.gettingVoltages = false;
        })
        .catch(() => {
          console.error('get_voltages response failed');
        });
    },

    onActivate() {
      this.activating = true;

      const options = {
        method: 'post',
        url: '/cmd/aligner_activate/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options)
        .then((res) => {
          this.activating = false;
        })
        .catch(() => {
          console.error('activate response failed');
        });
    },

    onGetCalibrationLogs() {
      this.gettingCalibrationLogs = true;

      const options = {
        method: 'post',
        url: '/cmd/aligner_get_calibration_logs/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options)
        .then((res) => {
          this.gettingCalibrationLogs = false;
        })
        .catch(() => {
          console.error('aligner_get_calibration_logs response failed');
        });
    },

    onGetScreenShot() {
      this.gettingScreenShot = true;

      const options = {
        method: 'post',
        url: '/cmd/aligner_get_screen_shot/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options)
        .then((res) => {
          this.gettingScreenShot = false;
        })
        .catch(() => {
          console.error('aligner_get_screen_shot response failed');
        });
    },

    onRcp() {
      this.settingRcp = true;

      const options = {
        method: 'post',
        url: '/cmd/aligner_rcp/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options)
        .then((res) => {
          this.settingRcp = false;
        })
        .catch(() => {
          console.error('aligner_rcp response failed');
        });
    },

    onSetLogLevel() {
      this.settingLogLevel = true;

      let loggingLevel = 'WARN';
      console.log('setting log level');
      if (this.aligner.logLevel === 'WARN') {
        console.log('raising log level to INFO');
        loggingLevel = 'INFO';
      } else {
        console.log('lowering log level to WARN');
      }

      const options = {
        method: 'post',
        url: '/cmd/aligner_set_logging_level/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
          loggingLevel,
        },
      };

      this.axios(options)
        .then((res) => {
          this.settingLogLevel = false;
        })
        .catch(() => {
          console.error('set logging level response failed');
        });
    },

    onClearPendingUpdates() {
      if (this.aligner) {
        console.log('clear pending updates');

        this.clearingPendingUpdates = true;

        const options = {
          method: 'post',
          url: '/cmd/clear_pending_updates/v1',
          data: {
            serialNumber: this.aligner.serialNumber,
          },
        };

        this.axios(options)
          .then(() => {
            alert('Pending updates cleared.');
          })
          .catch(() => {
            alert('Pending updates could not be cleared.');
          })
          .finally(() => {
            this.clearingPendingUpdates = false;
          });
      }
    },

    onStageUpdate() {
      let options;

      if (this.aligner.family === 'gtps') {
        options = {
          method: 'post',
          url: '/create/immediate_stage_of_gtps_software_update/v1',
          data: {
            serialNumber: this.aligner.serialNumber,
            version: {
              semantic: this.$store.state.latestGtpsSoftwareVersion.semantic,
              meta: this.$store.state.latestGtpsSoftwareVersion.meta,
            },
            path: this.$store.state.latestGtpsSoftwareVersion.path,
          },
        };
      } else if (this.aligner.family === 'aligner-oasis') {
        options = {
          method: 'post',
          url: '/create/immediate_stage_of_oasis_software_update/v1',
          data: {
            serialNumber: this.aligner.serialNumber,
            version: {
              semantic: this.$store.state.latestOasisSoftwareVersion.semantic,
              meta: this.$store.state.latestOasisSoftwareVersion.meta,
            },
            path: this.$store.state.latestOasisSoftwareVersion.path,
          },
        };
      } else {
        options = {
          method: 'post',
          url: '/create/immediate_stage_of_software_update/v1',
          data: {
            serialNumber: this.aligner.serialNumber,
            version: {
              semantic: this.$store.state.latestSoftwareVersion.semantic,
              meta: this.$store.state.latestSoftwareVersion.meta,
            },
            path: this.$store.state.latestSoftwareVersion.path,
          },
        };
      }

      this.axios(options)
        .then((res) => {
          this.aligner.pendingSoftwareDownload = res.data.version.semantic;
        });
    },

    onUnstageUpdate() {
      let options;

      if (this.aligner.family === 'gtps') {
        options = {
          method: 'post',
          url: '/delete/immediate_unstage_of_gtps_software_update/v1',
          data: {
            serialNumber: this.aligner.serialNumber,
          },
        };
      } else if (this.aligner.family === 'aligner-oasis') {
        options = {
          method: 'post',
          url: '/delete/immediate_unstage_of_oasis_software_update/v1',
          data: {
            serialNumber: this.aligner.serialNumber,
          },
        };
      } else {
        options = {
          method: 'post',
          url: '/delete/immediate_unstage_of_software_update/v1',
          data: {
            serialNumber: this.aligner.serialNumber,
          },
        };
      }

      this.axios(options)
        .then(() => {
          this.aligner.pendingSoftwareDownload = null;
        });
    },

    closeChangeTargetDialog(value) {
      console.log('closeChangeTargetDialog', value);

      this.changingTargets = true;

      const options = {
        method: 'post',
        url: '/cmd/set_target_eol_types/v1',
        data: {
          serialNumber: value.serialNumber,
          targetType: value.selectedTargetType,
        },
      };

      this.axios(options)
        .then(() => {
          console.log('greate success!!!')
          alert('Target type changed!');
        })
        .catch(() => {
          console.log('great failure!!!');
          alert('Target type could not be changed!');
        })
        .finally(() => {
          this.changingTargets = false;
        });

      this.showChangeTargetDialog = false;
    },

    closeSupportTicketDialog(value) {
      if (value) {
        this.aligner.openSupportTickets = this.aligner.openSupportTickets + 1;
      }

      this.showSupportTicketDialog = false;
    },

    closeResetSerialNumberDialog(value) {
      if (value) {
        this.resettingSerialNumber = true;

        const options = {
          method: 'post',
          url: '/cmd/reset_serial_number/v1',
          data: value,
        };

        this.axios(options)
          .then((res) => {
            this.resettingSerialNumber = false;
          });
      }

      this.showResetSerialNumberDialog = false;
    },

    toggleFavorite() {
      if (this.aligner.favorite) {
        this.deleteFavorite();
      } else {
        this.createFavorite();
      }
    },

    deleteFavorite() {
      const options = {
        method: 'post',
        url: '/delete/favorite/v1/',
        data: {
          username: this.$store.state.auth.token.user.username,
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options)
        .then((res) => {
          this.aligner.favorite = false;
        });
    },

    onOpenPort() {
      const options = {
        method: 'post',
        url: '/cmd/aligner_open_ssh_port/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options)
        .then((res) => {
        })
        .catch(() => {
          console.error('open_ssh_port response failed');
        });
    },

    createFavorite() {
      const options = {
        method: 'post',
        url: '/create/favorite/v1/',
        data: {
          username: this.$store.state.auth.token.user.username,
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options)
        .then((res) => {
          this.aligner.favorite = true;
        })
        .catch(() => {
          this.snackbarText = 'Favorite limit exceeded.';
          this.snackbar = true;
        });
    },

    onCheckNow() {
      const options = {
        method: 'post',
        url: '/cmd/aligner_check_now/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options);
    },

    onDownload() {
      const options = {
        method: 'post',
        url: '/cmd/aligner_download_now/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options);
    },

    onInstall() {
      const options = {
        method: 'post',
        url: '/cmd/aligner_install_now/v1',
        data: {
          serialNumber: this.aligner.serialNumber,
        },
      };

      this.axios(options);
    },
  },
};
</script>

<style scoped>
#leftDiv {
}

.data {
  font-weight: bold;
}
</style>
